import { useState, useEffect, useRef, useMemo } from 'react';
import { useViewsStore, useTradesFilterStore, useTradesDataStore, useCurrencyDisplayStore, useFxRatesStore } from '@/stores';
import { useRepoMarginCallBalance } from '@/hooks/trades/use-repo-margin-call-balance';
import { useTradesSummary } from '@/hooks/trades/use-trades-summary';
import { buildTradesMetricsParams, resolveViewCounterparties } from '@/utils/trades/map-server-params';
import { aggregateFromBuckets } from '@/utils/trades/currency-totals';
import { describeCurrencyTotal, type FooterColumnTotal } from '@/utils/trades/footer-totals';
import { bookBalanceCurrency } from '@/utils/trades/cash-pool-display';
import type { CounterpartySummary, MoneySummary } from '@/types';

/**
 * Summary metrics displayed in the MetricsBar above the trades table.
 *
 * LR-546: sourced from the server aggregate endpoint (`GET /positions/metrics/`,
 * via useTradesSummary) instead of derived from the whole in-memory book. The
 * backend returns per-counterparty, per-settlement-currency subtotals; this hook
 * selects the active view's counterparties, merges their subtotals, and rolls
 * them up to the display base with LIVE FX (describeCurrencyTotal) — so the
 * ≈-base display, per-currency tooltip and EUR/USD pill are unchanged. The
 * MetricsBar consumes the SAME ViewMetrics shape as before.
 *
 * See docs/planning/LR-104/customer-decisions.md §4–5 for the formulas behind
 * exposure and total value (the server replica lives in
 * apps/positions/services/metrics.py).
 */
export type MoneyTotal = FooterColumnTotal;

export interface ViewMetrics {
  /** Total trade count across the currently filtered set. */
  count: number;
  /** Trades in the attention statuses (pending / rejected — ATTENTION_STATUSES on
   *  both sides of the API) across the currently filtered set. Drives the
   *  MetricsBar "Attention" chip (LR-686); same server aggregate as `count`. */
  attentionCount: number;
  stockLB: {
    /** Trades in the lend+borrow family (ALL statuses — drives the count badge). */
    count: number;
    /** Trades in the lend+borrow family that are in the OPEN book — the presence
     *  signal for showing the section, matching the money cells' basis. */
    openCount: number;
    totalValueLent: MoneyTotal;
    totalValueBorrowed: MoneyTotal;
    exposureLent: MoneyTotal;
    exposureBorrowed: MoneyTotal;
  };
  repo: {
    /** Trades in the repo+reverse_repo family (ALL statuses — drives the count badge). */
    count: number;
    /** Repo+reverse trades in the OPEN book — the section presence signal. */
    openCount: number;
    totalValue: MoneyTotal;
    netExposure: MoneyTotal;
    /**
     * `null` when the active view is not scoped to exactly one counterparty
     * (all-trades, or a hypothetical multi-CP view). UI hides the cell.
     */
    cashpool: number | null;
    /** The currency `cashpool` is in: the counterparty's GMRA agreement currency. */
    cashpoolCurrency: string | null;
    /** The collateral sits in pools of several currencies: `cashpool` is null, yet calls exist. */
    cashpoolInSeveralCurrencies: boolean;
  };
}

export interface UseViewMetricsReturn {
  metrics: ViewMetrics;
  isFlashing: boolean;
  /** No summary yet: the figures are zeros standing in for data. A refetch keeps the
   *  previous summary, so this is the first load only. */
  isLoading: boolean;
  /** The first load failed: the zeros are not figures either. */
  isError: boolean;
}

const sum = (entries: CounterpartySummary[], pick: (e: CounterpartySummary) => number): number =>
  entries.reduce((acc, e) => acc + pick(e), 0);

/** Merge per-CP per-currency string maps into one native {ccy: number} map. */
const mergeMoney = (
  entries: CounterpartySummary[],
  pick: (e: CounterpartySummary) => MoneySummary,
): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const e of entries) {
    for (const [ccy, value] of Object.entries(pick(e))) {
      out[ccy] = (out[ccy] ?? 0) + parseFloat(value);
    }
  }
  return out;
};

/**
 * Computes grouped metrics for the active view plus a blue flash animation when
 * the active view changes.
 */
export const useViewMetrics = (): UseViewMetricsReturn => {
  const activeViewId = useViewsStore((state) => state.activeViewId);
  const views = useViewsStore((state) => state.views);
  const localTradesFilters = useTradesFilterStore((state) => state.localTradesFilters);
  const searchQuery = useTradesDataStore((state) => state.searchQuery);
  // Base for the multi-currency ≈ roll-up (EUR/USD pill toggle) + the reactive FX
  // signal, so the money cells re-roll when live rates land.
  const baseCurrency = useCurrencyDisplayStore((state) => state.baseCurrency);
  const fxVersion = useFxRatesStore((state) => state.version);

  // MetricsBar renders only on the LIVE blotter, so the summary is scoped to the
  // live (non-terminal) statuses + the sidebar filters + search, minus the
  // trade-type filter (the L/B & Repo sections ARE the trade-type selector).
  const metricsParams = useMemo(
    () => buildTradesMetricsParams({ localFilters: localTradesFilters, searchQuery, mode: 'trades' }),
    [localTradesFilters, searchQuery],
  );
  const { data: summary, isError: summaryFailed } = useTradesSummary('exposure', metricsParams);

  const activeView = views.find((v) => v.id === activeViewId);
  const cpNames = activeView?.counterparties ?? [];
  const sidebarCps = localTradesFilters.counterparties;
  const singleCpName = cpNames.length === 1 ? cpNames[0] : undefined;
  // Cashpool lives at the CP level, so it's only meaningful for a single-CP view.
  const { data: cashpoolBalance } = useRepoMarginCallBalance(singleCpName, !!singleCpName);

  const [isFlashing, setIsFlashing] = useState(false);
  const prevActiveViewIdRef = useRef<string>(activeViewId);
  useEffect(() => {
    if (prevActiveViewIdRef.current !== activeViewId) {
      prevActiveViewIdRef.current = activeViewId;
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), 600);
      return () => clearTimeout(timer);
    }
  }, [activeViewId]);

  const metrics = useMemo<ViewMetrics>(() => {
    // Same view∩sidebar CP resolution the list query uses, so bar and table agree.
    const all = summary?.by_counterparty ?? [];
    const cps = resolveViewCounterparties(cpNames, sidebarCps);
    const entries = cps === 'ALL'
      ? all
      : all.filter((e) => e.counterparty_name != null && cps.includes(e.counterparty_name!));

    const money = (pick: (e: CounterpartySummary) => MoneySummary): MoneyTotal =>
      describeCurrencyTotal(aggregateFromBuckets(mergeMoney(entries, pick), baseCurrency));

    const result: ViewMetrics = {
      count: sum(entries, (e) => e.count),
      attentionCount: sum(entries, (e) => e.attention_count),
      stockLB: {
        count: sum(entries, (e) => e.stock_lb.count),
        openCount: sum(entries, (e) => e.stock_lb.open_count),
        totalValueLent: money((e) => e.stock_lb.total_value_lent),
        totalValueBorrowed: money((e) => e.stock_lb.total_value_borrowed),
        exposureLent: money((e) => e.stock_lb.exposure_lent),
        exposureBorrowed: money((e) => e.stock_lb.exposure_borrowed),
      },
      repo: {
        count: sum(entries, (e) => e.repo.count),
        openCount: sum(entries, (e) => e.repo.open_count),
        totalValue: money((e) => e.repo.total_value),
        netExposure: money((e) => e.repo.net_exposure),
        cashpool: null,
        cashpoolCurrency: null,
        cashpoolInSeveralCurrencies: false,
      },
    };

    // Cashpool from the backend balance (LR-385): a numeric string (incl. "0.00")
    // → number; null (never called) or not-single-CP → null.
    if (singleCpName) {
      const raw = cashpoolBalance?.cashpool;
      result.repo.cashpool = raw == null ? null : Number(raw);
      // Blank when the counterparty has no GMRA agreement.
      result.repo.cashpoolCurrency = cashpoolBalance?.currency || null;
      result.repo.cashpoolInSeveralCurrencies =
        bookBalanceCurrency(cashpoolBalance?.pools ?? [], '') === null;
    }

    return result;
    // fxVersion is an intentional extra dep (unused in the body): the ≈ roll-ups read
    // live FX through currency-service module state, so this forces a recompute when
    // rates land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, cpNames.join(','), sidebarCps.join(','), singleCpName, cashpoolBalance, baseCurrency, fxVersion]);

  return {
    metrics,
    isFlashing,
    isLoading: summary === undefined && !summaryFailed,
    isError: summary === undefined && summaryFailed,
  };
};
